import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { ImageLoadingQueueService } from 'src/app/services/image-loading-queue.service';
import { UserService } from 'src/app/services/user.service';
import { ImageConfigurations } from '../items-canvas/board-items/board-image-configurations';
import { ResourceItemModel } from '../../../models/resource';
import { modifiedSetTimeout } from '../../../utilities/ZoneUtils';
import { LoadingLayoutType } from '../../../ui/loading/loading.component';

export enum ImageLoadingState {
  DidNotAttemptToLoad = 'DidNotAttemptToLoad',
  Loading = 'Loading',
  Loaded = 'Loaded',
  FailedToLoad = 'FailedToLoad',
}
@UntilDestroy()
@Component({
  selector: 'app-image-item-canvas',
  templateUrl: './image-item-canvas.component.html',
  styleUrls: ['./image-item-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageItemCanvasComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() state!: ImageConfigurations;
  @Input() static = false;
  @Input() customLoadingLayout = false;
  @Input() zoomLevel = 0;

  @Output() stateChanged = new EventEmitter<ImageConfigurations>();
  @Output() loadingStateChanged = new EventEmitter<ImageLoadingState>();
  @Output() loadedImageElement = new EventEmitter<HTMLImageElement>();

  @ViewChild('image') imageElement?: ElementRef<HTMLImageElement>;
  @ViewChild('reloadButton', { read: ElementRef }) reloadButtonElement?: ElementRef;
  @ViewChild('contentContainer') componentContainer?: ElementRef;

  protected readonly showUploadIndicator$ = new BehaviorSubject(false);
  protected readonly smallContainer$ = new BehaviorSubject<boolean>(false);
  public readonly imageLoadingState$ = new BehaviorSubject<ImageLoadingState>(
    ImageLoadingState.DidNotAttemptToLoad,
  );

  // When there are lots of images, to not fetch all of them, we show an HTML preview instead,
  // This will improve the performance especially for iPad (touch) devices
  protected readonly shouldShowPreviewInsteadOfImage$ = new BehaviorSubject<boolean>(true);

  private readonly MIN_ZOOM_LEVEL_TO_LOAD_IMAGES_IN_DEFAULT = 0.01;

  public readonly showLoadingIndicator$ = new BehaviorSubject<boolean>(true);

  showLoadingIndicatorOnLoading = this.flagService.isFlagEnabled(
    FLAGS.PROGRESS_INDICATOR_IMAGE_CANVAS,
  );

  private containerResizeObserver?: ResizeObserver;

  protected readonly ResourceItemModel = ResourceItemModel;
  protected readonly ImageLoadingState = ImageLoadingState;

  public readonly isTouchDevice = this.deviceAndBrowserDetectorService.isTouchDevice();
  private hasAcquiredLoadingSlot = false;

  constructor(
    private flagService: FlagsService,
    private userService: UserService,
    private imageLoadingQueueService: ImageLoadingQueueService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private cdr: ChangeDetectorRef,
  ) {
    this.imageLoadingState$.pipe(untilDestroyed(this)).subscribe((loadingState) => {
      this.loadingStateChanged.emit(loadingState);
      if (loadingState === ImageLoadingState.Loaded && this.imageElement?.nativeElement) {
        this.loadedImageElement.emit(this.imageElement?.nativeElement);
      }
    });

    combineLatest([
      this.imageLoadingState$,
      this.flagService.featureFlagChanged(FLAGS.RENDER_IMAGES_IN_FABRIC),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([loadingState, renderImagesInFabric]) => {
        if (renderImagesInFabric) {
          // We won't show the image if the rendering will happen on fabric side.
          // That is because we don't want to render the image in html, destroy it then render it in fabric.
          // We keep the loading indicator until the component is destroyed after the fabric loads the image
          this.showLoadingIndicator$.next(loadingState !== ImageLoadingState.FailedToLoad);
        } else {
          this.showLoadingIndicator$.next(
            loadingState !== ImageLoadingState.FailedToLoad &&
              loadingState !== ImageLoadingState.Loaded,
          );
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.static) {
      this.shouldShowPreviewInsteadOfImage$.next(false);
      return;
    }

    const newShowPreviewInsteadOfImage = this.showPreviewInsteadOfImage();
    if (!newShowPreviewInsteadOfImage && this.shouldShowPreviewInsteadOfImage$.getValue()) {
      // time to show the images since the canvas now fits them (probably does not contain lots of images)
      this.shouldShowPreviewInsteadOfImage$.next(false);
      this.handleIntersectionChange({ isIntersecting: true });
    }
  }

  showPreviewInsteadOfImage() {
    const minZoomLevel =
      this.flagService.featureFlagsVariables.enable_image_loading_queue
        ?.touch_devices_preview_min_zoom ?? this.MIN_ZOOM_LEVEL_TO_LOAD_IMAGES_IN_DEFAULT;

    return this.zoomLevel < minZoomLevel && this.isTouchDevice && this.static;
  }

  ngAfterViewInit() {
    this.showUploadIndicator$.next(this.state.ownerId === this.userService.userId);

    this.setUpResizeObserver();
  }

  ngOnDestroy(): void {
    this.containerResizeObserver?.disconnect();
    // In case a slot was acquired but the image did not finish loading yet,
    // make sure we always release any held slot when the component is destroyed.
    this.releaseLoadingSlot();
  }

  handleImageLoaded(): void {
    this.imageLoadingState$.next(ImageLoadingState.Loaded);
    this.releaseLoadingSlot();
  }

  handleImageLoadingFailure(): void {
    this.imageLoadingState$.next(ImageLoadingState.FailedToLoad);
    this.releaseLoadingSlot();
  }

  handleReloadImage(): void {
    if (!this.imageElement) {
      return;
    }
    this.imageLoadingState$.next(ImageLoadingState.Loading);

    modifiedSetTimeout(() => {
      if (!this.imageElement) {
        return;
      }

      const currentSrc = this.imageElement.nativeElement.src;
      this.imageElement.nativeElement.src = '';
      this.imageElement.nativeElement.src = currentSrc;
    }, 500);
  }

  handleIntersectionChange(event: { isIntersecting: boolean }): void {
    if (!event.isIntersecting) {
      return;
    }
    this.requestToStartLoading();
  }

  private requestToStartLoading(): void {
    const currentState = this.imageLoadingState$.getValue();
    if (currentState === ImageLoadingState.Loading || currentState === ImageLoadingState.Loaded) {
      // Already loading or loaded
      return;
    }

    if (!this.flagService.isFlagEnabled(FLAGS.ENABLE_IMAGE_LOADING_QUEUE)) {
      // If the feature flag is not enabled, we will start loading the image immediately.
      this.imageLoadingState$.next(ImageLoadingState.Loading);
      return;
    }

    this.imageLoadingQueueService
      .requestSlot()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        // for safety to avoid any race in case handleIntersectionChange fired several times
        // while the image is still DidNotAttemptToLoad.
        // Double check that we still need to load this image to not grant unneeded slots.
        const latestState = this.imageLoadingState$.getValue();
        if (
          latestState === ImageLoadingState.DidNotAttemptToLoad ||
          latestState === ImageLoadingState.FailedToLoad
        ) {
          this.imageLoadingState$.next(ImageLoadingState.Loading);
          this.hasAcquiredLoadingSlot = true;
        } else {
          // Slot was granted but we no longer need to start loading.
          this.imageLoadingQueueService.releaseSlot();
        }
      });
  }

  setUpResizeObserver() {
    // Deciding wither the container is small so that we hide certain element for responsiveness
    this.containerResizeObserver = new ResizeObserver(() => {
      const container = this.componentContainer?.nativeElement;
      if (!container) {
        return;
      }

      const height = container.clientHeight;
      const width = container.clientWidth;

      this.smallContainer$.next(height < 150 || width < 220);

      this.cdr.detectChanges();
    });

    this.containerResizeObserver.observe(this.componentContainer?.nativeElement);
  }

  private releaseLoadingSlot(): void {
    if (this.hasAcquiredLoadingSlot) {
      this.imageLoadingQueueService.releaseSlot();
      this.hasAcquiredLoadingSlot = false;
    }
  }

  protected readonly LoadingLayoutType = LoadingLayoutType;
}
